<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ContextModal from './logEntries/ContextModal.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faCalendarDay, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';

  let logEntries = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let isLoading = true;
  let searchText = '';
  let selectedEvent = 'ALL';
  let selectedLogLevel = 'ALL';
  let selectedDate = '';
  let filterSet = 'none';
  let isExporting = false;

  $: limit = selectedDate ? 999999 : 20;

  let isModalOpen = false;
  let selectedContext = '';

  function hasContextContent(context) {
    // Check if context exists and has any keys
    return context && Object.keys(context).length > 0;
  }

  function showModal(context) {
    if (hasContextContent(context)) {
      selectedContext = JSON.stringify(context);
      isModalOpen = true;
    }
  }

  function closeModal() {
    isModalOpen = false;
    selectedContext = '';
  }

  onMount(async () => {
    // Get date from URL parameter
    const urlDate = $page.url.searchParams.get('date');
    if (urlDate) {
      selectedDate = urlDate;
    }

    if ($selectedParticipant) {
      fetchLogEntries(1);
    }
  });

  // Update URL when date changes
  function handleDateChange() {
    const url = new URL(window.location);
    if (selectedDate) {
      url.searchParams.set('date', selectedDate);
    } else {
      url.searchParams.delete('date');
    }
    goto(url.toString(), { replaceState: true });
    fetchLogEntries(1);
  }

  async function fetchLogEntries(page = 1) {
    try {
      let url = `${API_BASE_URL}/logEntries?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}&searchText=${searchText}`;

      if (selectedLogLevel !== 'ALL') {
        url += `&logLevel=${selectedLogLevel}`;
      }

      if (selectedEvent !== 'ALL') {
        url += `&event=${selectedEvent}`;
      }

      if (selectedDate) {
        url += `&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;
      }

      url += `&filterSet=${filterSet}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch log entries');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      logEntries = responseData.data;
    } catch (err) {
      console.error('Error fetching log entries:', err);
      logEntries = [];
    }
  }

  function handleRefresh() {
    //console.log("handleRefresh");
    fetchLogEntries(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchLogEntries(newPage);
    }
  }

  function handleSearch() {
    fetchLogEntries(1);
  }

  function handleClearSearch() {
    searchText = '';
    fetchLogEntries(1);
  }

  function handleKeydown(event) {
    if (event.key === 'Enter') {
      handleSearch();
    }
  }

  async function fetchAllLogEntries() {
    try {
      let url = `${API_BASE_URL}/logEntries?page=1&pageSize=999999&subjectGUID=${$selectedParticipant._id}&searchText=${searchText}`;

      if (selectedLogLevel !== 'ALL') {
        url += `&logLevel=${selectedLogLevel}`;
      }

      if (selectedEvent !== 'ALL') {
        url += `&event=${selectedEvent}`;
      }

      if (selectedDate) {
        url += `&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;
      }

      url += `&filterSet=${filterSet}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all log entries');
      }

      const result = await res.json();
      return result.data || [];
    } catch (err) {
      console.error('Error fetching all log entries:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allEntries = await fetchAllLogEntries();

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        subjectId: $selectedParticipant?.subjectId,
        reportType: 'Log_Entries'
      });

      const formattedData = allEntries.map((item) => ({
        Subject: $selectedParticipant.subjectId || '',
        Platform: item.platform || '',
        Level: item.logLevel || '',
        Event: item.event || '',
        'Timestamp (Local)': item.timestamp
          ? formatToLocalDateTime(item.timestamp, $selectedParticipant.timezoneCode)
          : '',
        Timezone: $selectedParticipant.timezoneCode || '',
        'Timestamp (UTC)': item.timestamp ? formatToLocalDateTime(item.timestamp, 'UTC') : '',
        Message: item.message || '',
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4" />
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="Log Entries" 
  description="This page displays application log data from the participant's mobile device and app. You can filter by date, search for specific text, 
  and use predefined filters to find relevant entries. Click on events with context data (marked with ✪) to view additional details. Export data to Excel for further analysis."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Log Entries" itemCountTitle="Log Entries" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="text-m mb-0">
      Date
      <input
        type="date"
        bind:value={selectedDate}
        on:input={handleDateChange}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      />
      <button
        on:click={() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          selectedDate = `${year}-${month}-${day}`;
          handleDateChange();
        }}
        class="pagination mx-1 p-0"
        title="Set to today"
      >
        <FontAwesomeIcon icon={faCalendarDay} />
      </button>
    </div>

    <div class="text-m mb-0">
        <input
          type="text"
          bind:value={searchText}
          placeholder="Search"
          on:keydown={handleKeydown}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
    </div>

    <div class="text-m mb-0">
      Filter
      <select
        bind:value={filterSet}
        on:change={() => handleRefresh()}
        class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="recent">Recent</option>
        <option value="abbott">Abbott</option>
        <option value="none">None</option>
      </select>
    </div>

    <div class="text-black dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold w-20">Platform</th>
        <th class="px-4 py-2 text-left bold w-28">Level</th>
        <th class="px-4 py-2 text-left bold w-28">Event</th>
        <th class="px-4 py-2 text-left bold w-56">Timestamp (Local)</th>
        <th class="px-4 py-2 text-left bold">Message</th>
      </tr>
    </thead>
    <tbody>
      {#each logEntries as logEntry, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2 align-top w-20">{logEntry.platform}</td>
          <td class="px-4 py-2 align-top w-28">{logEntry.logLevel}</td>
          <td
            class="px-4 py-2 align-top w-28 {hasContextContent(logEntry.context)
              ? 'cursor-pointer'
              : ''}"
            on:click={() => showModal(logEntry.context)}
            >{logEntry.event || ''}{hasContextContent(logEntry.context) ? ' ✪' : ''}</td
          >
          <td class="px-4 py-2 w-56 align-top"
            >{logEntry.timestamp
              ? formatToLocalDateTime(logEntry.timestamp, $selectedParticipant.timezoneCode)
              : ''}</td
          >
          <td class="px-4 py-2 break-all align-top">{logEntry.message}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<ContextModal {isModalOpen} contextData={selectedContext} on:close={closeModal} />

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
